import { TelemetryEvent, cloneEvent } from './TelemetryEvent';
import { TenantTokenManager } from './TenantTokenManager';
import { DataField } from './DataField';
import { TelemetryEventValidator } from './TelemetryEventValidator';
import { Event } from './Event';
import { logNotification, LogLevel, Category, logError } from './OTelNotifications';
import { TelemetrySink } from './TelemetrySink';
import { makeStringDataField } from '.';
import { oteljsVersion } from './Version';

/**
 * Use this as the Nexus tenant token when Nexus logging is not needed or relevant
 */
export const SuppressNexus = -1;

export interface ISimpleTelemetryLogger {
  /**
   * onSendEvent: Events that fire whenever sendTelemetryEvent processes an event
   */
  onSendEvent: Event<TelemetryEvent>;

  /**
   * persistentDataFields: Data fields that will automatically be sent with every event
   */
  persistentDataFields: DataField[];

  /**
   * Send a telemetry event
   * @param event - The event to send
   */
  sendTelemetryEvent(event: TelemetryEvent): void;

  /**
   * Add a sink for processing telemetry events
   * @param sink - A telemetry sink to which events will be passed when sendTelemetryEvent is called.
   */
  addSink(sink: TelemetrySink): void;

  /**
   * Set tokens to use for sending telemetry to Aria and/or Nexus
   * The tokens will be used for events which whose namespace exactly matches
   * @param namespace - The namespace for the tokens
   * @param ariaTenantToken - Optional: The Aria tenant token
   * @param nexusTenantToken - Optional: The Nexus tenant token
   */
  setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number): void;

  /**
   * Set tokens to use for sending telemetry
   * The token tree is an object whose structure defines namespaces. At any level of the tree,
   * it can define values for 'ariaTenantToken' (string) and/or 'nexusTenantToken' (number).
   * These values are used as telemetry tokens for any events in that namespace or deeper.
   * @param tokenTree - An object containing tokens to use when sending telemetry
   */
  setTenantTokens(tokenTree: object): void;

  /**
   * Get the currently applied telemetry configuration
   */
  getConfig(): ITelemetryConfig;
}

/**
 * A configuration object for telemetry sinks
 */
export interface ITelemetryConfig {
  /**
   * NOT FOR USE IN BYPASSING VALIDATION RULES; ONLY FOR USE IN IMPROVING PERFORMANCE
   *
   * Disables basic validation that telemetry events conform to Office Event Schema
   * Validation adds a small overhead that could adversely affect perf in production.
   * In pre-production, validation should be turned on to ensure that events are valid.
   * To improve perf, validation can be turned off in production. This flag MUST NOT
   * be used to bypass rules that would otherwise prevent events from being sent.
   */
  disableValidation?: boolean;
}

/**
 * SimpleTelemetryLogger is the base implementation of a telemetry logger.
 * It exposes sendTelemetryEvent, which is used to log a telemetry event.
 */
export class SimpleTelemetryLogger implements ISimpleTelemetryLogger {
  /**
   * A list of sinks that telemetry events will be forwarded to
   */
  onSendEvent: Event<TelemetryEvent> = new Event<TelemetryEvent>();
  /**
   * @internal Internal variable; do not use publicly.
   */
  persistentDataFields: DataField[] = [];

  private config: ITelemetryConfig;

  /**
   * Creates a telemetry logger
   * @param parent - A parent telemetry logger that this telemetry logger should inherit from
   *                 This telemetry logger will send telemetry to the same sinks
   *                 and will send the same persistent data fields as the parent logger
   * @param persistentDataFields - Data fields to send with every event
   */
  constructor(parent?: ISimpleTelemetryLogger, persistentDataFields?: DataField[], config?: ITelemetryConfig) {
    this.config = config || {};
    if (parent) {
      this.onSendEvent = parent.onSendEvent;
      this.persistentDataFields.push(...parent.persistentDataFields);
      this.config = { ...parent.getConfig(), ...this.config };
    } else {
      this.persistentDataFields.push(makeStringDataField('OTelJS.Version', oteljsVersion));
    }
    if (persistentDataFields) {
      this.persistentDataFields.push(...persistentDataFields);
    }
  }

  /**
   * SendTelemetryEvent is the basic way to send a telemetry event.
   * This should be used for sending telemetry about discrete events that happened
   * For telemetry that includes duration and/or success/failure, the Activity API may be more appropriate.
   * @param event - The telemetry event to send
   */
  public sendTelemetryEvent(event: TelemetryEvent) {
    let localEvent: TelemetryEvent;
    try {
      if (this.onSendEvent.getListenerCount() === 0) {
        logNotification(LogLevel.Warning, Category.Core, () => 'No telemetry sinks are attached.');
        return;
      }

      // Don't modify the event that got passed in; we create our own.
      localEvent = this.cloneEvent(event);
      this.processTelemetryEvent(localEvent);
    } catch (error) {
      logError(Category.Core, 'SendTelemetryEvent', error);
      return;
    }

    try {
      this.onSendEvent.fireEvent(localEvent);
    } catch (_e) {
      // Sinks should handle their own errors;
    }
  }

  /**
   * Processes a telemetry event, but does not send it
   *   Determines the tenant tokens
   *   Adds persistent data fields
   *   Validates the event
   * Throws if the event is invalid
   * @param event - The telemetry event to process
   */
  public processTelemetryEvent(event: TelemetryEvent) {
    if (!event.telemetryProperties) {
      event.telemetryProperties = TenantTokenManager.getTenantTokens(event.eventName);
    }

    if (event.dataFields && this.persistentDataFields) {
      // The last-added data field wins, if the same-named data field is added more than once.
      // Persistent data fields should have lower priority than per-event data fields
      event.dataFields.unshift(...this.persistentDataFields);
    }

    if (!this.config.disableValidation) {
      TelemetryEventValidator.validateTelemetryEvent(event);
    }
  }

  /**
   * Adds a telemetry sink, to which events will be sent
   * @param sink - The telemetry sink to add
   */
  public addSink(sink: TelemetrySink) {
    this.onSendEvent.addListener(event => sink.sendTelemetryEvent(event as TelemetryEvent));
  }

  /**
   * Sets a tenant token for a single namespace. Events within the namespace will be sent to the corresponding tenant.
   * @param namespace - The namespace to which the tokens apply, such as Office.System.
   * @param ariaTenantToken - The Aria tenant token
   * @param nexusTenantToken - The Nexus tenant token
   */
  public setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number) {
    TenantTokenManager.setTenantToken(namespace, ariaTenantToken, nexusTenantToken);
  }

  /**
   * Sets tenant tokens for multiple namespaces.
   * @param tokenTree - An that represents namespaces and which tenants to send corresponding tokens to
   *   For example, the following tree identifies where to send events under the Office.System and Office.Word namespaces.
   *                \{ Office: \{
   *                    System: \{
   *                      ariaTenantToken: 'foo',
   *                      nexusTenantToken: 100
   *                    \},
   *                    Word: \{
   *                      ariaTenantToken: 'bar',
   *                      nexusTenantToken: 200
   *                    \}
   *                \}\}
   */
  public setTenantTokens(tokenTree: object) {
    TenantTokenManager.setTenantTokens(tokenTree);
  }

  /**
   * Clone a telemetry event; generally so we don't modify the input telemetry event
   * Also sets dataFields to [] if it isn't already set
   * @param event - The event to clone
   */
  public cloneEvent(event: TelemetryEvent): TelemetryEvent {
    return cloneEvent(event);
  }

  /**
   * Gets the currently applied configuration
   */
  public getConfig() {
    return this.config;
  }
}
